import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  Paper,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Android as BotIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';

interface ToolFunction {
  name: string;
}

interface ToolCall {
  function: ToolFunction;
}

interface ToolResult {
  success?: boolean;
  result?: unknown[];
}

interface Message {
  id?: string;
  role: string;
  content: string;
  timestamp: number | string;
  tool_calls?: ToolCall[];
  tool_results?: ToolResult[];
}

interface ChatInterfaceProps {
  conversation: Message[];
  onSendMessage?: (message: string) => void;
  onClearConversation?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  chatContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    height: '60vh',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  messageList: {
    flex: 1,
    overflow: 'auto',
    padding: 0,
    backgroundColor: theme.palette.background.default,
  },
  inputContainer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'flex-end' as const,
    gap: theme.spacing(1),
  },
  messageItem: {
    display: 'flex',
    marginBottom: theme.spacing(1),
    alignItems: 'flex-start' as const,
  },
  userMessage: {
    justifyContent: 'flex-end' as const,
  },
  assistantMessage: {
    justifyContent: 'flex-start' as const,
  },
  messagePaper: {
    padding: theme.spacing(2),
    maxWidth: '80%',
    wordBreak: 'break-word' as const,
  },
  userMessagePaper: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantMessagePaper: {
    backgroundColor: theme.palette.background.paper,
  },
  markdownContent: {
    minHeight: '40px',
    '& p': {
      margin: 0,
      marginBottom: theme.spacing(1),
      '&:last-child': { marginBottom: 0 },
    },
    '& ul, & ol': { margin: theme.spacing(1, 0), paddingLeft: theme.spacing(3) },
    '& li': { marginBottom: theme.spacing(0.5) },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: theme.spacing(1, 0),
      '&:first-child': { marginTop: 0 },
    },
    '& code': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.shape.borderRadius / 2,
      fontSize: '0.875em',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
      '& code': { backgroundColor: 'transparent', padding: 0 },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.grey[300]}`,
      paddingLeft: theme.spacing(2),
      margin: theme.spacing(1, 0),
      fontStyle: 'italic' as const,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse' as const,
      margin: theme.spacing(1, 0),
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(0.5, 1),
      textAlign: 'left' as const,
    },
    '& th': {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 'bold',
    },
  },
  messageIcon: {
    margin: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  toolCallsContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  toolCallChip: {
    margin: theme.spacing(0.5),
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  inputField: {
    flex: 1,
  },
}));

const ChatInterface = ({
  conversation,
  onSendMessage,
  onClearConversation,
}: ChatInterfaceProps): React.JSX.Element => {
  const classes = useStyles();
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation.length, scrollToBottom]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setInputText(event.target.value);
    },
    [],
  );

  const handleSendMessage = useCallback((): void => {
    if (inputText.trim() && onSendMessage) {
      onSendMessage(inputText.trim());
      setInputText('');
    }
  }, [inputText, onSendMessage]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const formatTimestamp = useCallback((timestamp: number | string): string => {
    return new Date(timestamp).toLocaleTimeString();
  }, []);

  const renderToolCalls = useCallback(
    (toolCalls: ToolCall[], toolResults?: ToolResult[]): React.JSX.Element | null => {
      if (!toolCalls || toolCalls.length === 0) return null;

      return (
        <Box className={classes.toolCallsContainer}>
          <Typography variant="caption" gutterBottom>
            <CodeIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }} />
            Tool Calls:
          </Typography>
          {toolCalls.map((call, index) => (
            <Chip
              key={index}
              label={call.function.name}
              size="small"
              variant="outlined"
              className={classes.toolCallChip}
            />
          ))}
          {toolResults && toolResults.length > 0 && (
            <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
              Found{' '}
              {toolResults.reduce((total: number, result) => {
                if (result.success && Array.isArray(result.result)) {
                  return total + result.result.length;
                }
                return total;
              }, 0)}{' '}
              results
            </Typography>
          )}
        </Box>
      );
    },
    [classes.toolCallsContainer, classes.toolCallChip],
  );

  const renderMessage = useCallback(
    (message: Message, index: number): React.JSX.Element => {
      const isUser = message.role === 'user';

      return (
        <ListItem
          key={message.id ?? index}
          className={`${classes.messageItem} ${isUser ? classes.userMessage : classes.assistantMessage}`}
        >
          {!isUser && <BotIcon className={classes.messageIcon} color="primary" />}

          <Paper
            className={`${classes.messagePaper} ${
              isUser ? classes.userMessagePaper : classes.assistantMessagePaper
            }`}
          >
            {isUser ? (
              <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                {message.content}
              </Typography>
            ) : (
              <Box className={classes.markdownContent}>
                <ReactMarkdown>{message.content}</ReactMarkdown>
              </Box>
            )}

            {message.tool_calls &&
              renderToolCalls(message.tool_calls, message.tool_results)}

            <Typography className={classes.timestamp}>
              {formatTimestamp(message.timestamp)}
            </Typography>
          </Paper>

          {isUser && <PersonIcon className={classes.messageIcon} color="secondary" />}
        </ListItem>
      );
    },
    [classes, renderToolCalls, formatTimestamp],
  );

  return (
    <Box className={classes.chatContainer}>
      <Box className={classes.headerActions}>
        <Typography variant="subtitle2" color="textSecondary">
          Chat History ({conversation.length} messages)
        </Typography>
        <IconButton size="small" onClick={onClearConversation} title="Clear conversation">
          <ClearIcon />
        </IconButton>
      </Box>

      <List className={classes.messageList}>
        {conversation.map((message, index) => renderMessage(message, index))}
        <div ref={messagesEndRef} />
      </List>

      <Box className={classes.inputContainer}>
        <TextField
          className={classes.inputField}
          multiline
          maxRows={3}
          variant="outlined"
          placeholder="Ask me about stashers, stashcards, or vendors..."
          value={inputText}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSendMessage}
          disabled={!inputText.trim()}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default ChatInterface;
